import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  Tabs, TabList, TabPanels, Tab, TabPanel,
  Alert, AlertIcon, Box, Button, ButtonGroup, Checkbox, HStack, Input, Spinner, Stack, Text,
} from "@chakra-ui/react";
import { fetchSmsSpend } from "../../services/smsSpend";
import OverviewPanel from "./smsSpend/OverviewPanel";
import PeoplePanel from "./smsSpend/PeoplePanel";
import PatientsPanel from "./smsSpend/PatientsPanel";
import WastePanel from "./smsSpend/WastePanel";
import WhenPanel from "./smsSpend/WhenPanel";
import { DIRECTIONS, count, dateTime, directionVerb, money, shortDay } from "./smsSpend/format";

// SMS Spend (SuperAdmin > Compliance & Monitoring): what texting costs, from Twilio's bill and its
// record of every text (backend/api/sms_spend.py). One date range above everything scopes every
// tab, and so do the direction (texts sent, received or both) and the scope: the texts this
// database has a record of, or, ticked, every text on the Twilio account (other servers share
// it). While a new range loads, the last one stays on screen, dimmed.

const PRESETS = [[7, "7 days"], [30, "30 days"], [90, "90 days"], [180, "6 months"], [365, "12 months"]];
const TABS = ["overview", "people", "patients", "waste", "when"];

function rangeWords(report) {
  if (!report?.range) return "";
  return `${shortDay(report.range.start)} – ${shortDay(report.range.end)} · ${report.range.days} days`;
}

function scopeWords(report) {
  const verb = directionVerb(report);
  return report?.include_outside ? `Every text ${verb} on the Twilio account` : `Texts ${verb} that this database has a record of`;
}

export default function SmsSpendModal({ isOpen, onClose, load = fetchSmsSpend }) {
  const [range, setRange] = useState({ days: 30 });
  const [direction, setDirection] = useState("outbound");
  const [outside, setOutside] = useState(false);        // also count texts this database has no record of
  const [custom, setCustom] = useState(null);           // {start, end} while the custom inputs show
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ignored, setIgnored] = useState(false);        // the server answered for another direction
  const [tabIndex, setTabIndex] = useState(0);
  const latest = useRef(0);

  const run = useCallback(async (refresh = false) => {
    const id = latest.current + 1;
    latest.current = id;
    setLoading(true);
    setError(null);
    try {
      const data = await load({ ...range, outside, direction, refresh });
      if (latest.current === id) {
        setReport(data);
        // A server started before the direction filter existed answers without `direction`.
        setIgnored(Boolean(data) && data.direction !== direction);
      }
    } catch (err) {
      if (latest.current === id) setError(err?.message || "The SMS spend couldn't be loaded. Try again.");
    } finally {
      if (latest.current === id) setLoading(false);
    }
  }, [load, range, outside, direction]);

  useEffect(() => {
    if (isOpen) run(false);
  }, [isOpen, run]);

  const showTab = (key) => setTabIndex(Math.max(0, TABS.indexOf(key)));
  const presetActive = (days) => !range.start && range.days === days;
  const leftOut = report && !report.include_outside ? report.tracking?.outside : null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb={1}>
          SMS Spend
          <Text fontSize="sm" fontWeight="normal" color="gray.500">
            {`${scopeWords(report)}, priced from Twilio's records${report ? ` · ${rangeWords(report)} · Pacific time` : ""}`}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <HStack spacing={2} mb={4} flexWrap="wrap" align="center">
            <ButtonGroup size="sm" isAttached variant="outline">
              {PRESETS.map(([days, label]) => (
                <Button key={days} variant={presetActive(days) ? "solid" : "outline"} colorScheme="pink"
                        aria-pressed={presetActive(days)}
                        onClick={() => { setCustom(null); setRange({ days }); }}>
                  {label}
                </Button>
              ))}
              <Button variant={range.start || custom ? "solid" : "outline"} colorScheme="pink"
                      onClick={() => setCustom(custom ? null : { start: range.start || "", end: range.end || "" })}>
                Custom
              </Button>
            </ButtonGroup>
            {custom && (
              <HStack spacing={2}>
                <Input type="date" size="sm" w="150px" value={custom.start} aria-label="From"
                       onChange={(e) => setCustom({ ...custom, start: e.target.value })} />
                <Input type="date" size="sm" w="150px" value={custom.end} aria-label="To"
                       onChange={(e) => setCustom({ ...custom, end: e.target.value })} />
                <Button size="sm" isDisabled={!custom.start || !custom.end}
                        onClick={() => setRange({ start: custom.start, end: custom.end })}>
                  Apply
                </Button>
              </HStack>
            )}
            <ButtonGroup size="sm" isAttached variant="outline" role="group" aria-label="Direction">
              {DIRECTIONS.map(([key, label, help]) => (
                <Button key={key} variant={direction === key ? "solid" : "outline"} colorScheme="pink"
                        aria-pressed={direction === key} title={help} onClick={() => setDirection(key)}>
                  {label}
                </Button>
              ))}
            </ButtonGroup>
            <Checkbox size="sm" colorScheme="pink" isChecked={outside} onChange={(e) => setOutside(e.target.checked)}>
              Include texts outside this database
            </Checkbox>
            {leftOut?.texts > 0 && (
              <Text fontSize="xs" color="gray.500" data-testid="sms-left-out">
                {count(leftOut.texts)} texts ({money(leftOut.cost)}) left out
              </Text>
            )}
            <Box flex="1" />
            {report?.read_at &&<Text fontSize="xs" color="gray.500">Read {dateTime(report.read_at)}</Text>}
            <Button size="sm" variant="outline" onClick={() => run(true)} isLoading={loading && Boolean(report)}>
              Refresh
            </Button>
          </HStack>

          {error && <Alert status="error" mb={4}><AlertIcon />{error}</Alert>}
          {ignored && (
            <Alert status="warning" mb={4} data-testid="sms-direction-ignored">
              <AlertIcon />
              The server ignored the Outbound / Inbound filter: it is running code from before the filter was added.
              Restart the backend (run_services.py) and press Refresh.
            </Alert>
          )}
          {!report && loading && (
            <HStack spacing={3} py={10} justify="center">
              <Spinner />
              <Text color="gray.500">Reading Twilio's records…</Text>
            </HStack>
          )}
          {report && (
            <Box opacity={loading ? 0.5 : 1} transition="opacity 0.2s" aria-busy={loading}>
              {(report.warnings || []).length > 0 && (
                <Alert status="warning" mb={4} alignItems="flex-start">
                  <AlertIcon />
                  <Stack spacing={0}>{report.warnings.map((w) => <Text key={w} fontSize="sm">{w}</Text>)}</Stack>
                </Alert>
              )}
              {report.truncated && (
                <Alert status="warning" mb={4}><AlertIcon />Only the newest texts are counted: pick a shorter range for all of them.</Alert>
              )}
              <Tabs colorScheme="pink" isLazy index={tabIndex} onChange={setTabIndex}>
                <TabList flexWrap="wrap">
                  <Tab>Overview</Tab>
                  <Tab>People &amp; clinics</Tab>
                  <Tab>Patients</Tab>
                  <Tab>Waste</Tab>
                  <Tab>When</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel px={0}><OverviewPanel report={report} onShowTab={showTab} /></TabPanel>
                  <TabPanel px={0}><PeoplePanel report={report} /></TabPanel>
                  <TabPanel px={0}><PatientsPanel report={report} /></TabPanel>
                  <TabPanel px={0}><WastePanel report={report} /></TabPanel>
                  <TabPanel px={0}><WhenPanel report={report} /></TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
